/*
 * Navigation bridge.
 *
 * Sagas need to navigate, but they run outside React and so cannot call
 * `useNavigate()`. Under react-router v5 this was solved by
 * `connected-react-router`, which made navigation a Redux action
 * (`put(push('/x'))`) by mirroring the router into the store. That package is
 * v5-only and unmaintained (last publish 2022), and the Redux router state it
 * maintained was never read anywhere in this app — so it was removed rather
 * than replaced.
 *
 * This module is the minimal thing that replaces it: a single module-level
 * reference to react-router's `navigate`, registered by <NavigationBridge/>
 * (rendered inside <BrowserRouter/> in routes.js) once the router mounts.
 *
 * Deliberately NOT built on a shared `history` object: react-router v7 exposes
 * history injection only via `unstable_HistoryRouter` /
 * `UNSAFE_createBrowserHistory`, and the public gallery is an anonymous
 * customer-facing surface that should not ride on prefixed-unstable APIs.
 */

let navigator = null;

// A navigation requested before the router mounted. `configureStore` runs
// `sagaMiddleware.run(rootSaga)` before ReactDOM renders, so a boot saga can
// genuinely ask to redirect before <NavigationBridge/> has registered. Rather
// than drop that redirect, hold the most recent one and replay it on register.
// Last-wins: only the final destination matters, and a single slot cannot grow.
let pending = null;

/**
 * Registers react-router's `navigate` function. Called by <NavigationBridge/>.
 * Passing `null` (on unmount) clears it.
 */
export const setNavigator = (fn) => {
  navigator = fn;

  if (fn && pending) {
    const { to, options } = pending;
    pending = null;
    fn(to, options);
  }
};

/**
 * Navigate from outside the React tree (sagas).
 *
 * @param {string|number} to  path, or a delta such as -1 for "back"
 * @param {object} [options]  react-router navigate options, e.g. { replace: true }
 */
export const navigate = (to, options) => {
  if (!navigator) {
    pending = { to, options };
    return;
  }
  navigator(to, options);
};

/** Test seam: drop any queued navigation between cases. */
export const resetNavigation = () => {
  navigator = null;
  pending = null;
};
